import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeftIcon, CheckCircleIcon, MapPinIcon, ClockIcon, BriefcaseIcon, MailIcon, SendIcon, UploadIcon, UserIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
// Vacancy data store
const vacanciesData: Record<string, any> = {
  'stage-design': {
    title: 'Stage Design',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['UI/UX', 'Figma', 'Webdesign', 'Branding'],
    intro: 'Bij Noord Digital ontwerp je als design-stagiair mee aan websites en merken voor B2B-bedrijven. Je werkt nauw samen met Alex en het team aan échte projecten.',
    responsibilities: ['Wireframes en prototypes ontwerpen in Figma', 'Visuele uitwerkingen maken voor klantwebsites', 'Meedenken over UX en conversie', 'Visuele assets en iconen ontwerpen', 'Bijdragen aan de doorontwikkeling van het Noord Digital merk'],
    requirements: ['Je volgt een MBO/HBO opleiding (CMD, Mediavormgeving, Graphic Design)', 'Ervaring met Figma', 'Gevoel voor typografie, kleur en compositie', 'Proactief en leergierig', 'Goede beheersing van het Nederlands'],
    offer: ['Passende stagevergoeding', 'Begeleiding door ervaren designers', 'Erkende leerplek (S-BB)', 'Werken aan projecten die echt live gaan', 'Uitzicht op een vaste (bij)baan'],
    status: 'bezet'
  },
  'stage-account-manager': {
    title: 'Stage Account Manager',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['Sales', 'Klantcontact', 'Communicatie', 'Project coördinatie'],
    intro: 'Als stage account manager ben je de schakel tussen onze klanten en het Noord Digital team. Je leert hoe je sales, klantcontact en interne afstemming combineert tot tevreden klanten en gezonde groei.',
    responsibilities: ['Eerste aanspreekpunt voor klanten via mail, telefoon en gesprekken', 'Sales gesprekken voorbereiden en bijwonen', 'Behoeftes van klanten vertalen naar duidelijke briefings voor het team', 'Voortgang van projecten bewaken en intern coördineren', 'Meedenken over het verbeteren van het sales- en accountproces'],
    requirements: ['Je volgt een MBO/HBO opleiding (Commerciële Economie, Communicatie, Bedrijfskunde)', 'Sterk in communicatie, mondeling én schriftelijk', 'Energiek, ondernemend en niet bang om de telefoon te pakken', 'Goede beheersing van het Nederlands', 'Gestructureerde werkwijze'],
    offer: ['Passende stagevergoeding', 'Direct contact met echte klanten en deals', 'Begeleiding door Rocco (oprichter)', 'Erkende leerplek (S-BB)', 'Uitzicht op een vaste (bij)baan'],
    status: 'open'
  },
  'stage-developer': {
    title: 'Stage Developer',
    type: 'Stage',
    location: 'Amsterdam / Hybride',
    hours: '32 - 40 uur',
    tags: ['React', 'TypeScript', 'WordPress', 'Tailwind CSS'],
    intro: 'Bouw mee aan snelle, schaalbare websites en webapps voor B2B-klanten. Deze stage opent over een aantal maanden. Meld je nu vast aan en wij tippen je zodra de plek open is.',
    responsibilities: ['Meebouwen aan klantwebsites in WordPress en React', 'Pixel-perfect Figma designs omzetten naar code', 'Performance en Core Web Vitals optimaliseren', 'Bestaande projecten onderhouden en updaten', 'Meedenken over technische keuzes'],
    requirements: ['Je volgt een MBO/HBO opleiding (Software Development, Informatica)', 'Basis kennis van HTML, CSS, JavaScript', 'Ervaring met React of WordPress is een plus', 'Leergierig en oog voor detail', 'Goede beheersing van het Nederlands'],
    offer: ['Passende stagevergoeding', 'Begeleiding door ervaren developers', 'Erkende leerplek (S-BB)', 'Werken aan projecten die live gaan', 'Uitzicht op een vaste (bij)baan'],
    status: 'binnenkort',
    opensAt: '2026-09-05T00:00:00'
  }
};
export const VacaturePage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const vacancy = slug ? vacanciesData[slug] : null;
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    motivatie: ''
  });
  const [fileName, setFileName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  // Redirect to overview if vacancy not found
  useEffect(() => {
    if (!vacancy && slug) {
      navigate('/sollicitatie', {
        replace: true
      });
    }
  }, [vacancy, slug, navigate]);
  // Countdown logic
  useEffect(() => {
    if (vacancy?.status === 'binnenkort' && vacancy.opensAt) {
      const targetDate = new Date(vacancy.opensAt).getTime();
      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;
        if (distance > 0) {
          setTimeLeft({
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
            minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
            seconds: Math.floor(distance % (1000 * 60) / 1000)
          });
        }
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [vacancy]);
  if (!vacancy) {
    return null; // Will redirect
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
    }
  };
  const renderCountdown = () => <div className="grid grid-cols-4 gap-2 text-center mt-4 mb-6" data-id="element-2397">
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2398">
        <div className="text-xl font-bold text-amber-700" data-id="element-2399">{timeLeft.days}</div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2400">
          Dagen
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2401">
        <div className="text-xl font-bold text-amber-700" data-id="element-2402">{timeLeft.hours}</div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2403">
          Uren
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2404">
        <div className="text-xl font-bold text-amber-700" data-id="element-2405">
          {timeLeft.minutes}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2406">
          Min
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-2" data-id="element-2407">
        <div className="text-xl font-bold text-amber-700" data-id="element-2408">
          {timeLeft.seconds}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold" data-id="element-2409">
          Sec
        </div>
      </div>
    </div>;
  return <div className="w-full min-h-screen bg-[#F7F8FA] text-[#111827] font-sans" data-id="element-2410">
      <SEO title={`${vacancy.title} | Werken bij Noord Digital`} description={vacancy.intro} keywords={`${vacancy.title.toLowerCase()}, vacature, werken bij noord digital, amsterdam`} canonical={`https://noorddigital.nl/vacature/${slug}`} data-id="element-2411" />
      <Header data-id="element-2412" />

      {/* Hero Section */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] pt-24 pb-32 relative overflow-hidden" data-id="element-2413">
        <div className="absolute inset-0 opacity-10" data-id="element-2414">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2415"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1000px] relative z-10" data-id="element-2416">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-8" data-id="element-2417">
            <a href="/sollicitatie" className="inline-flex items-center text-white/80 hover:text-white transition-colors text-sm font-medium" data-id="element-2418">
              <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2419" />
              Terug naar alle vacatures
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-2420">
            <div className="flex flex-wrap gap-3 mb-6" data-id="element-2421">
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2422">
                <BriefcaseIcon className="mr-2 h-4 w-4" data-id="element-2423" />
                {vacancy.type}
              </span>
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2424">
                <MapPinIcon className="mr-2 h-4 w-4" data-id="element-2425" />
                {vacancy.location}
              </span>
              <span className="inline-flex items-center bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm" data-id="element-2426">
                <ClockIcon className="mr-2 h-4 w-4" data-id="element-2427" />
                {vacancy.hours}
              </span>
              {vacancy.status === 'bezet' && <span className="inline-flex items-center bg-gray-500/80 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm border border-gray-400" data-id="element-2428">
                  Op dit moment bezet
                </span>}
              {vacancy.status === 'binnenkort' && <span className="inline-flex items-center bg-amber-500/80 text-white px-3 py-1 rounded-full text-sm font-bold backdrop-blur-sm border border-amber-400" data-id="element-2429">
                  Opent 5 september 2026
                </span>}
            </div>

            <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2430">
              {vacancy.title}
            </h1>

            <div className="flex flex-wrap gap-2 mb-8" data-id="element-2431">
              {vacancy.tags.map((tag: string, index: number) => <span key={index} className="bg-[#005FCC] text-white text-sm px-3 py-1 rounded-md font-medium" data-id="element-2432">
                  {tag}
                </span>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1000px] -mt-16 relative z-20 pb-24" data-id="element-2433">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2434">
          {/* Main Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="lg:col-span-2 space-y-8" data-id="element-2435">
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100" data-id="element-2436">
              <h2 className="text-2xl font-bold text-[#111827] mb-4" data-id="element-2437">
                Over de functie
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-2438">
                {vacancy.intro}
              </p>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2439">
                Wat ga je doen?
              </h3>
              <ul className="space-y-3 mb-8" data-id="element-2440">
                {vacancy.responsibilities.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2441">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2442" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2443">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2444">
                Wat wij zoeken
              </h3>
              <ul className="space-y-3 mb-8" data-id="element-2445">
                {vacancy.requirements.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2446">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2447" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2448">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <h3 className="text-xl font-bold text-[#111827] mb-4 mt-8" data-id="element-2449">
                Wat wij bieden
              </h3>
              <ul className="space-y-3" data-id="element-2450">
                {vacancy.offer.map((item: string, index: number) => <li key={index} className="flex items-start" data-id="element-2451">
                    <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mr-3 mt-0.5 flex-shrink-0" data-id="element-2452" />
                    <span className="text-[#6B7280] leading-relaxed" data-id="element-2453">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-1" data-id="element-2454">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 sticky top-28" data-id="element-2455">
              {vacancy.status === 'open' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2456">
                    Enthousiast geworden?
                  </h3>
                  <p className="text-[#6B7280] mb-6 text-sm" data-id="element-2457">
                    Vul het sollicitatieformulier in en we nemen snel contact
                    met je op.
                  </p>

                  <a href="#solliciteer-formulier" className="flex items-center justify-center w-full bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold py-4 px-6 rounded-xl transition duration-300 shadow-md group mb-4" data-id="element-2458">
                    <MailIcon className="mr-2 h-5 w-5" data-id="element-2459" />
                    Solliciteer direct
                  </a>

                  <p className="text-xs text-center text-gray-500" data-id="element-2460">
                    We proberen binnen 2 werkdagen te reageren.
                  </p>
                </>}

              {vacancy.status === 'bezet' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2461">
                    Helaas, deze plek is bezet
                  </h3>
                  <p className="text-[#6B7280] mb-6 text-sm" data-id="element-2462">
                    We hebben momenteel al een topper gevonden voor deze stage.
                    Houd onze website in de gaten voor toekomstige plekken.
                  </p>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-center mb-4" data-id="element-2463">
                    <span className="text-gray-500 font-medium" data-id="element-2464">
                      Solliciteren is nu niet mogelijk
                    </span>
                  </div>
                </>}

              {vacancy.status === 'binnenkort' && <>
                  <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-2465">
                    Deze stage opent binnenkort
                  </h3>
                  <p className="text-[#6B7280] mb-2 text-sm" data-id="element-2466">
                    Zet het in je agenda! Vanaf 5 september 2026 kun je
                    solliciteren op deze stageplek.
                  </p>

                  {renderCountdown()}

                  <a href="mailto:info@noorddigital.nl?subject=Interesse%20in%20Stage%20Developer" className="flex items-center justify-center w-full bg-white border-2 border-[#007AFF] text-[#007AFF] hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl transition duration-300 mb-4" data-id="element-2467">
                    <MailIcon className="mr-2 h-5 w-5" data-id="element-2468" />
                    Laat je gegevens achter
                  </a>
                </>}

              <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-2469">
                <div className="flex items-center gap-4" data-id="element-2470">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-2471">
                    <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-full h-full object-cover" data-id="element-2472" />
                  </div>
                  <div data-id="element-2473">
                    <p className="text-sm font-bold text-[#111827]" data-id="element-2474">
                      Vragen over deze rol?
                    </p>
                    <a href="mailto:info@noorddigital.nl" className="text-sm text-[#007AFF] hover:underline" data-id="element-2475">
                      Stuur ons een berichtje
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Conditional Form Section */}
      <div id="solliciteer-formulier" className="w-full bg-white py-20 md:py-24" data-id="element-2476">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[700px]" data-id="element-2477">
          {vacancy.status === 'open' ? <>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-2478">
                <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2479">
                  Solliciteer op {vacancy.title}
                </h2>
                <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2480">
                  Vul onderstaand formulier in en voeg je CV toe. We nemen
                  binnen 2 werkdagen contact met je op.
                </p>
              </motion.div>

              {isSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-2481">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2482">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-2483" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-2484">
                    Bedankt voor je sollicitatie!
                  </h3>
                  <p className="text-[#6B7280] text-lg mb-8" data-id="element-2485">
                    We hebben je gegevens ontvangen en nemen binnen 2 werkdagen
                    contact met je op.
                  </p>
                  <a href="/sollicitatie" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors" data-id="element-2486">
                    <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2487" />
                    Bekijk alle vacatures
                  </a>
                </motion.div> : <motion.form initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} onSubmit={handleSubmit} className="bg-[#F7F8FA] rounded-2xl p-8 md:p-10 border border-gray-100" data-id="element-2488">
                  <div className="space-y-6" data-id="element-2489">
                    {/* Naam */}
                    <div data-id="element-2490">
                      <label htmlFor="naam" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2491">
                        Naam *
                      </label>
                      <div className="relative" data-id="element-2492">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2493" />
                        <input type="text" id="naam" required value={formData.naam} onChange={e => setFormData({
                    ...formData,
                    naam: e.target.value
                  })} placeholder="Je volledige naam" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2494" />
                      </div>
                    </div>

                    {/* Email */}
                    <div data-id="element-2495">
                      <label htmlFor="email" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2496">
                        E-mailadres *
                      </label>
                      <div className="relative" data-id="element-2497">
                        <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2498" />
                        <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} placeholder="je@email.nl" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2499" />
                      </div>
                    </div>

                    {/* Telefoon */}
                    <div data-id="element-2500">
                      <label htmlFor="telefoon" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2501">
                        Telefoonnummer
                      </label>
                      <div className="relative" data-id="element-2502">
                        <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2503" />
                        <input type="tel" id="telefoon" value={formData.telefoon} onChange={e => setFormData({
                    ...formData,
                    telefoon: e.target.value
                  })} placeholder="06 - 12345678" className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" data-id="element-2504" />
                      </div>
                    </div>

                    {/* Vacature (read-only) */}
                    <div data-id="element-2505">
                      <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2506">
                        Vacature
                      </label>
                      <div className="relative" data-id="element-2507">
                        <BriefcaseIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2508" />
                        <input type="text" readOnly value={vacancy.title} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-[#6B7280] cursor-not-allowed" data-id="element-2509" />
                      </div>
                    </div>

                    {/* CV Upload */}
                    <div data-id="element-2510">
                      <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2511">
                        CV uploaden *
                      </label>
                      <label htmlFor="cv-upload" className="flex items-center justify-center w-full py-8 bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#007AFF] hover:bg-blue-50/30 transition-all group" data-id="element-2512">
                        <div className="text-center" data-id="element-2513">
                          <UploadIcon className="h-8 w-8 text-gray-400 group-hover:text-[#007AFF] mx-auto mb-2 transition-colors" data-id="element-2514" />
                          {fileName ? <p className="text-sm font-medium text-[#007AFF]" data-id="element-2515">
                              {fileName}
                            </p> : <>
                              <p className="text-sm font-medium text-[#6B7280]" data-id="element-2516">
                                Klik om je CV te uploaden
                              </p>
                              <p className="text-xs text-gray-400 mt-1" data-id="element-2517">
                                PDF, DOC of DOCX (max. 5MB)
                              </p>
                            </>}
                        </div>
                        <input type="file" id="cv-upload" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" data-id="element-2518" />
                      </label>
                    </div>

                    {/* Motivatie */}
                    <div data-id="element-2519">
                      <label htmlFor="motivatie" className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-2520">
                        Korte motivatie
                      </label>
                      <textarea id="motivatie" rows={5} value={formData.motivatie} onChange={e => setFormData({
                  ...formData,
                  motivatie: e.target.value
                })} placeholder="Vertel kort waarom je bij Noord Digital wilt werken en wat je motiveert voor deze rol..." className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[#111827] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all resize-none" data-id="element-2521" />
                    </div>
                  </div>

                  {/* Submit */}
                  <button type="submit" className="mt-8 w-full flex items-center justify-center bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl group" data-id="element-2522">
                    <SendIcon className="mr-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-2523" />
                    Verstuur sollicitatie
                  </button>

                  <p className="text-xs text-center text-gray-500 mt-4" data-id="element-2524">
                    Door te solliciteren ga je akkoord met ons{' '}
                    <a href="/privacy" className="text-[#007AFF] hover:underline" data-id="element-2525">
                      privacybeleid
                    </a>
                    .
                  </p>
                </motion.form>}
            </> : vacancy.status === 'bezet' ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-2526">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2527">
                <BriefcaseIcon className="h-8 w-8 text-gray-500" data-id="element-2528" />
              </div>
              <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-2529">
                Deze stage is op dit moment bezet
              </h3>
              <p className="text-[#6B7280] text-lg mb-8 max-w-lg mx-auto" data-id="element-2530">
                We hebben momenteel al een geschikte kandidaat gevonden voor de
                rol van {vacancy.title}. Houd onze website in de gaten voor
                toekomstige stagemogelijkheden.
              </p>
              <a href="/sollicitatie" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors" data-id="element-2531">
                <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-2532" />
                Terug naar alle vacatures
              </a>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#F7F8FA] rounded-2xl p-12 text-center border border-gray-100" data-id="element-2533">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2534">
                <ClockIcon className="h-8 w-8 text-amber-600" data-id="element-2535" />
              </div>
              <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-2536">
                Stage opent op 5 september 2026
              </h3>
              <p className="text-[#6B7280] text-lg mb-8 max-w-lg mx-auto" data-id="element-2537">
                Deze stageplek is momenteel nog niet open voor sollicitaties. Je
                kunt ons wel alvast een berichtje sturen als je interesse hebt!
              </p>
              <div className="max-w-md mx-auto mb-8" data-id="element-2538">{renderCountdown()}</div>
              <a href="mailto:info@noorddigital.nl?subject=Interesse%20in%20Stage%20Developer" className="inline-flex items-center justify-center bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 shadow-md" data-id="element-2539">
                <MailIcon className="mr-2 h-5 w-5" data-id="element-2540" />
                Laat je gegevens achter
              </a>
            </motion.div>}
        </div>
      </div>

      <Footer data-id="element-2541" />
    </div>;
};